/**
 * The trust's statutory year, as a live list rather than static prose.
 *
 * Before this (2026-08-06) the SARS deadlines existed on the tax screen only as
 * two explanatory paragraphs — correct, and completely inert. Nothing counted
 * down to them and nothing on the trust overview knew they existed.
 *
 * The footer note is not boilerplate: Elreign cannot see SARS eFiling, so this
 * card is a calendar, never a compliance record. See `lib/statutory-calendar.ts`
 * for why a passed SARS date reads "was due" and never "overdue".
 */

import {
  upcomingObligations,
  obligationBadgeLabel,
  type StatutoryObligation,
} from '../lib/statutory-calendar';
import { Icon } from './icon';

interface ToneStyle {
  bg: string;
  fg: string;
}

function toneFor(o: StatutoryObligation): ToneStyle {
  switch (o.state) {
    case 'overdue':
      return { bg: 'var(--negative-soft)', fg: 'var(--negative)' };
    case 'due_soon':
      return { bg: 'var(--accent-100)', fg: 'var(--accent-700)' };
    case 'upcoming':
      return { bg: 'var(--surface-2)', fg: 'var(--ink-2)' };
    case 'passed':
      return { bg: 'var(--surface-2)', fg: 'var(--ink-3)' };
  }
}

function ObligationRow({ o, last }: { o: StatutoryObligation; last: boolean }) {
  const tone = toneFor(o);
  return (
    <li
      data-testid={`obligation-${o.id}`}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 18,
        padding: '16px 28px',
        borderBottom: last ? 'none' : '1px solid var(--hairline)',
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 14.5, color: 'var(--ink)', fontWeight: 500 }}>{o.label}</div>
        <div
          style={{
            fontSize: 13,
            color: 'var(--ink-3)',
            lineHeight: 1.55,
            marginTop: 3,
          }}
        >
          {o.detail}
        </div>
      </div>
      <span
        style={{
          flexShrink: 0,
          fontSize: 11.5,
          fontWeight: 600,
          padding: '4px 11px',
          borderRadius: 999,
          background: tone.bg,
          color: tone.fg,
          whiteSpace: 'nowrap',
        }}
      >
        {obligationBadgeLabel(o)}
      </span>
    </li>
  );
}

export interface StatutoryCalendarCardProps {
  /** The trust's stored next FICA review date. Omit on screens where the FICA
   *  cycle is not in scope (the tax screen shows the SARS ring only). */
  ficaReviewOn?: string | null;
  /** How many upcoming obligations to list. */
  count?: number;
  /** Injectable clock — tests pass a fixed instant. */
  nowMs?: number;
}

export function StatutoryCalendarCard({
  ficaReviewOn,
  count = 4,
  nowMs,
}: StatutoryCalendarCardProps) {
  const items = upcomingObligations(
    { ...(nowMs !== undefined ? { nowMs } : {}), ...(ficaReviewOn ? { ficaReviewOn } : {}) },
    count,
  );

  return (
    <section className="card" style={{ padding: 0 }} data-testid="statutory-calendar">
      <div
        style={{
          padding: '22px 28px',
          borderBottom: '1px solid var(--hairline)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <h3 className="serif-heading" style={{ fontWeight: 400, fontSize: 22, margin: 0 }}>
          The statutory year
        </h3>
        <span className="card-meta" style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <Icon name="clock" size={13} />
          What is coming up
        </span>
      </div>

      {items.length === 0 ? (
        <div style={{ padding: 36, textAlign: 'center', color: 'var(--ink-3)', fontSize: 13.5 }}>
          Nothing scheduled in the window.
        </div>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {items.map((o, i) => (
            <ObligationRow key={`${o.id}-${o.dueOn}`} o={o} last={i === items.length - 1} />
          ))}
        </ul>
      )}

      <div
        style={{
          padding: '16px 28px',
          borderTop: '1px solid var(--hairline)',
          background: 'var(--surface)',
          fontSize: 13,
          color: 'var(--ink-3)',
          lineHeight: 1.6,
        }}
      >
        These are the statutory dates for this trust. Elreign does not file
        returns and cannot see SARS eFiling, so this is a calendar, not a
        record of what your tax practitioner has already submitted.
      </div>
    </section>
  );
}
